import React, { useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useForm, FormProvider } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQueryClient } from "@tanstack/react-query";
import * as z from "zod";
import {
  DEFAULT_FORM_VALUES,
  VALIDATION_MESSAGES,
} from "../../constants/autostack";
import { addToAutoStack } from "../../services/autostackService";
import StackMoreStep from "../../components/StackMoreStep";
import ConfirmStackMoreStep from "../../components/ConfirmStackMoreStep";
import StackMoreStatusStep from "../../components/StackMoreStatusStep";
import {
  useQueryDigitalWallet,
  useUserSingleAutoStack,
} from "../../hooks/query/queries";
import { fundingTypeMap } from "../Autostack";

const createStackMoreFormSchema = (portfolioEstimate: number | null) =>
  z
    .object({
      stackAmount: z
        .string()
        .min(1, VALIDATION_MESSAGES.REQUIRED_AMOUNT)
        .refine((val) => {
          const num = Number(val.replace(/,/g, ""));
          return !isNaN(num) && num > 0;
        }, VALIDATION_MESSAGES.INVALID_AMOUNT_MIN),
      stackType: z.enum(["wallet", "bank"]),
    })
    .refine(
      (data) => {
        if (data.stackType === "wallet" && portfolioEstimate !== null) {
          const stackAmount = Number(data.stackAmount.replace(/,/g, ""));
          return stackAmount <= portfolioEstimate;
        }
        return true;
      },
      {
        message: VALIDATION_MESSAGES.INSUFFICIENT_BALANCE,
        path: ["stackAmount"],
      }
    );

export type StackMoreFormData = z.infer<
  ReturnType<typeof createStackMoreFormSchema>
>;
export type StackMoreStepType = "amount-setup" | "confirm" | "status";

const StackMore: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { stackId } = useParams();
  const [currentStep, setCurrentStep] =
    useState<StackMoreStepType>("amount-setup");
  const [isSuccess, setIsSuccess] = useState<boolean>(true);
  const { digitalWalletData } = useQueryDigitalWallet();
  const { data: userSingleAutoStack } = useUserSingleAutoStack(stackId);

  const fiatBalance =
    digitalWalletData?.balances?.assetBalances?.fiat?.[0]?.availableAmount ||
    null;

  const form = useForm<StackMoreFormData>({
    resolver: zodResolver(createStackMoreFormSchema(fiatBalance)),
    defaultValues: {
      stackAmount: DEFAULT_FORM_VALUES.stackAmount,
      stackType: DEFAULT_FORM_VALUES.stackType,
    },
    mode: "onSubmit",
  });

  const getFieldsForStep = useCallback(
    (step: StackMoreStepType): (keyof StackMoreFormData)[] => {
      switch (step) {
        case "amount-setup":
          return ["stackAmount", "stackType"];
        case "confirm":
          return [];
        default:
          return [];
      }
    },
    []
  );

  const handleFinalSubmit = useCallback(async () => {
    const isValid = await form.trigger();

    if (!isValid) {
      return;
    }

    try {
      const formData = form.getValues();

      const stackType = fundingTypeMap[formData.stackType];

      if (stackId) {
        await addToAutoStack(stackId, {
          amount: parseFloat(formData.stackAmount),
          stackType,
        });

        setIsSuccess(true);
        setCurrentStep("status");

        await Promise.all([
          queryClient.invalidateQueries({
            queryKey: ["userAutoStacks"],
          }),
          queryClient.invalidateQueries({
            queryKey: ["autoStackStats"],
          }),
          queryClient.invalidateQueries({ queryKey: ["currencies"] }),
          queryClient.invalidateQueries({ queryKey: ["notifications"] }),
        ]);
      } else {
        throw new Error("No stack ID provided");
      }
    } catch (error) {
      console.error("Error adding more to stack:", error);
      setIsSuccess(false);
      setCurrentStep("status");
    }
  }, [form, queryClient, stackId]);

  const handleStepComplete = useCallback(async () => {
    const isValid = await form.trigger(getFieldsForStep(currentStep));

    if (!isValid) {
      return;
    }

    if (currentStep === "amount-setup") {
      setCurrentStep("confirm");
    } else if (currentStep === "confirm") {
      await handleFinalSubmit();
    }
  }, [currentStep, form, getFieldsForStep, handleFinalSubmit]);

  const handleStepBack = useCallback(() => {
    if (currentStep === "status") {
      setCurrentStep("confirm");
    } else if (currentStep === "confirm") {
      setCurrentStep("amount-setup");
    } else if (currentStep === "amount-setup") {
      navigate("/autostack");
    }
  }, [currentStep, navigate]);

  const handleCancel = useCallback(() => {
    navigate("/autostack");
  }, [navigate]);

  const handleStatusContinue = useCallback(() => {
    if (isSuccess) {
      navigate(`/autostack/${stackId}`);
    } else {
      setCurrentStep("confirm");
    }
  }, [isSuccess, navigate, stackId]);

  const renderCurrentStep = useCallback(() => {
    const commonProps = {
      onStepComplete: handleStepComplete,
      onStepBack: handleStepBack,
      onCancel: handleCancel,
      currentStep,
    };

    switch (currentStep) {
      case "amount-setup":
        return <StackMoreStep {...commonProps} />;

      case "confirm":
        return (
          <ConfirmStackMoreStep
            {...commonProps}
            asset={userSingleAutoStack?.asset}
          />
        );

      case "status":
        return (
          <StackMoreStatusStep
            isSuccess={isSuccess}
            onContinue={handleStatusContinue}
            asset={userSingleAutoStack?.asset}
          />
        );

      default:
        return null;
    }
  }, [
    handleStepComplete,
    handleStepBack,
    handleCancel,
    currentStep,
    userSingleAutoStack?.asset,
    isSuccess,
    handleStatusContinue,
  ]);

  return <FormProvider {...form}>{renderCurrentStep()}</FormProvider>;
};

export default StackMore;
